import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { profile } from '../data/profile';

/**
 * Museum-style portrait treatment: a gold rule frame over a white matte,
 * offset backing plates, corner brackets, an engraved monogram seal, and a
 * plaque carrying the name and title.
 *
 * NOTE ON SIZE: the supplied headshot is 200x200px. The frame is deliberately
 * capped near that native size so the image stays sharp instead of being
 * upscaled into softness. If a larger source file arrives, raise the max-width.
 */
export function FramedPortrait() {
  const reduceMotion = useReducedMotion();
  return <motion.div className="relative mx-auto w-full max-w-[316px]" initial={reduceMotion ? undefined : {
    opacity: 0,
    scale: 0.97
  }} animate={reduceMotion ? undefined : {
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.6,
    delay: 0.1,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-308" data-mp="1lipj2hne4vdz">
      <div className="absolute -bottom-5 -left-5 h-full w-full border border-metal/35" aria-hidden="true" data-id="element-309" data-mp="298jt6rgbs5uj" />
      <div className="absolute -right-4 -top-4 h-full w-full bg-navy-soft" aria-hidden="true" data-id="element-310" data-mp="2sjzt4rmlu05" />

      <div className="absolute -left-3 -top-3 z-20 h-14 w-14 border-l-2 border-t-2 border-metal" aria-hidden="true" data-id="element-311" data-mp="7q3do3kew4rk" />
      <div className="absolute -right-3 -top-3 z-20 h-14 w-14 border-r-2 border-t-2 border-metal" aria-hidden="true" data-id="element-312" data-mp="3u67q3zuwtl0n" />
      <div className="absolute -bottom-3 -left-3 z-20 h-14 w-14 border-b-2 border-l-2 border-metal" aria-hidden="true" data-id="element-313" data-mp="3g5wteg6x5c7k" />
      <div className="absolute -bottom-3 -right-3 z-20 h-14 w-14 border-b-2 border-r-2 border-metal" aria-hidden="true" data-id="element-314" data-mp="37603kb8sm7ax" />

      <div className="relative z-10 bg-white p-3 shadow-[0_30px_70px_-15px_rgba(3,10,22,0.75)]" data-id="element-315" data-mp="3b8n1mtw24ogk">
        <div className="border-[1.5px] border-metal/60 p-1.5" data-id="element-316" data-mp="15fok8klgw4au">
          <div className="relative bg-canvas" data-id="element-317" data-mp="19fytc64k3atx">
            <img src={profile.portraitUrl} alt={`Portrait of ${profile.name}`} className="aspect-square w-full object-cover object-center [filter:contrast(1.06)_saturate(1.04)]" width={280} height={280} data-id="element-318" data-mp="19fp1eczyatll" />
            <span className="absolute inset-0 border border-white/40" aria-hidden="true" data-id="element-319" data-mp="29da6yucyismu" />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-3 border-t border-line px-1 pb-0.5 pt-3" data-id="element-320" data-mp="24f8oaajg0vbw">
          <div className="min-w-0" data-id="element-321" data-mp="315redklc0g7z">
            <span className="block font-serif text-[14px] font-semibold leading-tight text-navy" data-id="element-322" data-mp="2x1jnphvbcq74">
              {profile.name}
            </span>
            <span className="mt-1 block truncate text-[9px] font-medium uppercase tracking-label text-charcoal-muted" data-id="element-323" data-mp="2s9s7o19sa2v8">
              EVP, CPO &amp; Global Corporate Services
            </span>
          </div>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-metal font-serif text-[11px] font-semibold text-metal" aria-hidden="true" data-id="element-324" data-mp="3ksyv3p1y5xp8">
            KB
          </span>
        </div>
      </div>

      <div className="relative z-10 mt-5 flex items-stretch border border-metal/30 bg-navy-soft" data-id="element-325" data-mp="3sc877q1u8ahz">
        <div className="flex flex-1 flex-col border-r border-metal/25 px-3.5 py-3.5" data-id="element-326" data-mp="13syr1zpzslm">
          <span className="font-serif text-base font-semibold leading-none text-white" data-id="element-327" data-mp="56phcsp9fopk">39+</span>
          <span className="mt-1.5 text-[9px] font-medium uppercase tracking-label text-white/55" data-id="element-328" data-mp="a6bvd4cwx8zf">
            Years
          </span>
        </div>
        <div className="flex flex-1 flex-col border-r border-metal/25 px-3.5 py-3.5" data-id="element-329" data-mp="f2k685k2ug0s">
          <span className="font-serif text-base font-semibold leading-none text-white" data-id="element-330" data-mp="jvvdu8wpr0tr">45+</span>
          <span className="mt-1.5 text-[9px] font-medium uppercase tracking-label text-white/55" data-id="element-331" data-mp="otx0t4x9fkai">
            Countries
          </span>
        </div>
        <div className="flex flex-1 flex-col px-3.5 py-3.5" data-id="element-332" data-mp="xuv23fgbddpe">
          <span className="font-serif text-base font-semibold leading-none text-metal-light" data-id="element-333" data-mp="xt1w82diyxig">
            $1B+
          </span>
          <span className="mt-1.5 text-[9px] font-medium uppercase tracking-label text-white/55" data-id="element-334" data-mp="g72fpgel5s2d">
            Spend
          </span>
        </div>
      </div>
    </motion.div>;
}